import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, ArrowUpRight, Lock, ShieldCheck, FileText, Globe } from "lucide-react";
import Logo from "@ui/Logo";
import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../context/AuthContext";
import InternalLink from "../components/InternalLink";
import AuthLanguagePicker from "./AuthLanguagePicker";
import AuthFaq from "./AuthFaq";
import { track, trackSignup } from "@crm/analytics";
import { generateDeviceFingerprint } from "../utils/deviceFingerprint";
import LoginV3Card from "./LoginV3Card";
import LoginV3Loading from "./LoginV3Loading";
import { getLastLoginMethod, setLastLoginMethod } from "./lastLoginMethod";
import { markOAuthFlight, consumeOAuthFlight } from "./loginOauthFlight";

function LoginV3() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const token = searchParams.get("token");
    const magicToken = searchParams.get("magicToken");
    const isNewUser = searchParams.get("isNewUser");
    const isShopifySignup = searchParams.get("isShopifySignup");

    const savedOtpState = React.useMemo(() => {
        try {
            const raw = sessionStorage.getItem("login_otp_state");
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            const elapsed = Math.floor((Date.now() - parsed.codeRequestedAt) / 1000);
            if (elapsed > 300) {
                sessionStorage.removeItem("login_otp_state");
                return null;
            }
            return { ...parsed, elapsed };
        } catch {
            return null;
        }
    }, []);

    const [email, setEmail] = React.useState(savedOtpState?.email || "");
    const [isTokenStep, setIsTokenStep] = React.useState(!!savedOtpState);
    const [error, setError] = React.useState(null);
    const [successMessage, setSuccessMessage] = React.useState(
        savedOtpState ? savedOtpState.successMessage : null
    );
    const [resendCountdown, setResendCountdown] = React.useState(() => {
        if (!savedOtpState) return 0;
        const remaining = savedOtpState.countdownDuration - savedOtpState.elapsed;
        return remaining > 0 ? remaining : 0;
    });
    const [resendAttempts, setResendAttempts] = React.useState(
        savedOtpState?.resendAttempts || 0
    );
    const [isResending, setIsResending] = React.useState(false);
    const [ssoAvailable, setSsoAvailable] = React.useState(false);
    const [isSSOLoading, setIsSSOLoading] = React.useState(false);
    const [isShopifyLoading, setIsShopifyLoading] = React.useState(false);
    const [isMagicLinkLoading, setIsMagicLinkLoading] = React.useState(false);
    const [isTokenLoading, setIsTokenLoading] = React.useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = React.useState(false);
    const [isMicrosoftLoading, setIsMicrosoftLoading] = React.useState(false);
    const [lastUsedMethod] = React.useState(() => getLastLoginMethod());

    const didTokenRequest = React.useRef(false);
    const didMagicLinkRequest = React.useRef(false);
    const didShopifyAuthRequest = React.useRef(false);

    const { getUser, setToken, isLoadingUser, user } = useAuth();

    const storedPath = location.state?.from;
    const invalidRedirectPaths = ["/", "/logout", "/signup", "/signup2"];
    const from =
        storedPath && !invalidRedirectPaths.includes(storedPath) ? storedPath : null;

    const { call: login, error: loginError, isLoading: isLoginLoading } =
        useBluelagoon({ method: "login" });
    const { call: requestCode, error: requestCodeError, isLoading: requestCodeIsLoading } =
        useBluelagoon({ method: "userRequestCode" });
    const { call: checkSSO } = useBluelagoon({ method: "checkSSOByEmail" });
    const { call: ssoLogin } = useBluelagoon({ method: "ssoLoginUser" });
    const { call: getGoogleUrl } = useBluelagoon({ method: "getGoogleAuthUrlForLogin" });
    const { call: getMicrosoftUrl } = useBluelagoon({ method: "getMicrosoftAuthUrlForLogin" });
    const { call: shopifyAuth } = useBluelagoon({ method: "shopifyAuthHandler" });
    const { call: authenticateWithMagicLink } = useBluelagoon({
        method: "authenticateWithMagicLink",
    });

    React.useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, []);

    React.useEffect(() => {
        track("login_page_visited", {
            hasToken: !!token,
            hasShopifyParams: !!(searchParams.get("hmac") && searchParams.get("shop")),
            referrer: document.referrer || "direct",
            variant: "v3",
        });
    }, []);

    const handleGetUser = React.useCallback(() => {
        return getUser()
            .then((u) => {
                if (!u) throw new Error("User data missing after authentication");
                track("login_user_retrieved", {
                    userId: u.id,
                    userRoles: u.roles,
                    tutorialCompleted: u.tutorials?.welcome?.completed,
                    isClient: u.roles?.includes("client"),
                });
                const defaultPath = u.roles?.includes("client")
                    ? "/client/workflows"
                    : u.interfaceStyle === "expert"
                        ? "/workflows"
                        : "/chat";
                const redirectPath = from || defaultPath;
                track("login_redirect", {
                    destination: redirectPath,
                    userType: u.roles?.includes("client")
                        ? "client"
                        : u.tutorials?.welcome?.completed
                            ? "returning_user"
                            : "new_user",
                    hadStoredPath: !!from,
                });
                if (isShopifySignup === "true" && isNewUser === "true") {
                    navigate("/integrate?token=true&isNewUser=true", {
                        state: {
                            openNewIntegrationModal: true,
                            preselectIntegrationTypeId: "shopify",
                        },
                    });
                    return u;
                }
                if (isNewUser === "true") trackSignup(email);
                navigate(redirectPath);
                return u;
            })
            .catch((err) => {
                track("login_user_retrieval_failed", { error: err?.message || "Unknown" });
                setError(
                    err?.message ||
                        t("auth.login.userRetrievalFailed", {
                            defaultValue:
                                "We signed you in but couldn't load your profile. Please try again.",
                        })
                );
                throw err;
            });
    }, [getUser, navigate, isNewUser, isShopifySignup, from, email, t]);

    const handleEmailSubmit = async (event) => {
        event.preventDefault();
        if (requestCodeIsLoading || isLoadingUser) return;
        const data = Object.fromEntries(new FormData(event.target).entries());
        track("login_attempt_started", { email: data.email, method: "email_token" });
        try {
            const response = await requestCode(data);
            const message = response?.message || t("auth.login.codeSent");
            setIsTokenStep(true);
            setError(null);
            setSuccessMessage(message);
            setResendCountdown(30);
            setResendAttempts(0);
            sessionStorage.setItem(
                "login_otp_state",
                JSON.stringify({
                    email: data.email,
                    codeRequestedAt: Date.now(),
                    countdownDuration: 30,
                    resendAttempts: 0,
                    successMessage: message,
                })
            );
            track("login_code_requested", { email: data.email, method: "email_token" });
        } catch (err) {
            track("login_code_request_failed", {
                email: data.email,
                method: "email_token",
                error: err?.message || "Unknown",
            });
        }
    };

    const handleTokenSubmit = async (event) => {
        event.preventDefault();
        if (isLoginLoading) return;
        const credentials = Object.fromEntries(new FormData(event.target).entries());
        credentials.email = email;
        track("login_token_submitted", { email: credentials.email, method: "email_token" });
        try {
            const response = await login({
                ...credentials,
                deviceFingerprint: generateDeviceFingerprint(),
            });
            setToken(response?.accessToken);
            sessionStorage.removeItem("login_otp_state");
            setLastLoginMethod("email");
            track("login_success", { email: credentials.email, method: "email_token" });
            await handleGetUser();
        } catch (err) {
            track("login_failed", {
                email: credentials.email,
                method: "email_token",
                error: err?.message || "Unknown",
            });
            setError(err);
        }
    };

    React.useEffect(() => {
        if (resendCountdown > 0) {
            const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCountdown]);

    const handleResendCode = async () => {
        if (resendCountdown > 0 || isResending) return;
        setIsResending(true);
        track("login_code_resend_requested", {
            email,
            method: "email_token",
            attempt: resendAttempts + 1,
        });
        try {
            const response = await requestCode({ email });
            const message = response?.message || t("auth.login.codeSent");
            setSuccessMessage(message);
            setResendAttempts((prev) => prev + 1);
            setResendCountdown(120);
            sessionStorage.setItem(
                "login_otp_state",
                JSON.stringify({
                    email,
                    codeRequestedAt: Date.now(),
                    countdownDuration: 120,
                    resendAttempts: resendAttempts + 1,
                    successMessage: message,
                })
            );
            track("login_code_resend_success", {
                email,
                method: "email_token",
                attempt: resendAttempts + 1,
            });
        } catch (err) {
            setError(err);
            track("login_code_resend_failed", {
                email,
                method: "email_token",
                attempt: resendAttempts + 1,
                error: err?.message || "Unknown",
            });
        } finally {
            setIsResending(false);
        }
    };

    React.useEffect(() => {
        if (!email || !email.includes("@") || isTokenStep) {
            setSsoAvailable(false);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const res = await checkSSO({ email: email.trim() });
                setSsoAvailable(!!res?.data?.ssoEnabled);
            } catch {
                setSsoAvailable(false);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [email, isTokenStep]);

    const handleSSOLogin = async () => {
        if (!ssoAvailable || !email) return;
        setIsSSOLoading(true);
        track("login_attempt_started", { method: "sso", email });
        try {
            const res = await ssoLogin({ email: email.trim() });
            if (res?.data?.authUrl) {
                setLastLoginMethod("sso");
                markOAuthFlight("sso");
                track("sso_auth_url_generated", { method: "sso" });
                window.location.href = res.data.authUrl;
                return;
            }
            setError(res?.data?.error || res?.error || t("auth.login.ssoLoginFailed"));
            track("login_failed", { method: "sso", error: res?.data?.error });
        } catch (err) {
            setError(err?.message || t("auth.login.ssoLoginFailed"));
            track("login_failed", { method: "sso", error: err?.message });
        }
        setIsSSOLoading(false);
    };

    React.useEffect(() => {
        const ssoError = searchParams.get("sso_error");
        if (ssoError) setError(ssoError);
    }, [searchParams]);

    React.useEffect(() => {
        const checkEmail = searchParams.get("check_email");
        if (checkEmail) {
            setSuccessMessage(
                t(
                    "auth.checkInboxToActivate",
                    `We sent a confirmation link to ${checkEmail}. Click it to finish setting up your AgentUI account.`,
                    { email: checkEmail }
                )
            );
        }
    }, [searchParams, t]);

    const handleGoogleSignIn = async () => {
        setIsGoogleLoading(true);
        track("login_attempt_started", { method: "google" });
        try {
            const res = await getGoogleUrl({ isAuth: true });
            if (res.status === 200) {
                setLastLoginMethod("google");
                markOAuthFlight("google");
                track("google_auth_url_generated", { method: "google" });
                window.location.href = res.data.url;
            } else {
                setError(res.data.error);
                track("google_auth_url_failed", { method: "google", error: res.data.error });
            }
        } catch (err) {
            setError(err);
            track("login_failed", { method: "google", error: err?.message || "Unknown" });
        } finally {
            setIsGoogleLoading(false);
        }
    };

    const handleMicrosoftSignIn = async () => {
        setIsMicrosoftLoading(true);
        track("login_attempt_started", { method: "microsoft" });
        try {
            const res = await getMicrosoftUrl({ isAuth: true });
            if (res.status === 200) {
                setLastLoginMethod("microsoft");
                markOAuthFlight("microsoft");
                track("microsoft_auth_url_generated", { method: "microsoft" });
                window.location.href = res.data.url;
            } else {
                setError(res.data.error);
                track("microsoft_auth_url_failed", { method: "microsoft", error: res.data.error });
            }
        } catch (err) {
            setError(err);
            track("login_failed", { method: "microsoft", error: err?.message || "Unknown" });
        } finally {
            setIsMicrosoftLoading(false);
        }
    };

    const handleShopifyAuth = React.useCallback(async () => {
        const hmac = searchParams.get("hmac");
        const host = searchParams.get("host");
        const shop = searchParams.get("shop");
        const timestamp = searchParams.get("timestamp");
        if (!(hmac && host && shop && timestamp)) return;
        setIsShopifyLoading(true);
        track("login_attempt_started", { method: "shopify", shop });
        try {
            const response = await shopifyAuth({ hmac, host, shop, timestamp });
            if (response?.data?.accessToken) {
                setToken(response.data.accessToken);
                track("login_success", {
                    method: "shopify",
                    shop,
                    hasRedirect: !!response.data.redirectTo,
                });
                if (response.data.redirectTo) window.location.href = response.data.redirectTo;
                handleGetUser();
            } else if (response.data.redirectTo) {
                track("shopify_redirect_without_token", { method: "shopify", shop });
                window.location.href = response.data.redirectTo;
            } else {
                const errorMessage = t("auth.login.shopifyAuthFailedNoToken");
                setError(errorMessage);
                track("login_failed", { method: "shopify", shop, error: errorMessage });
            }
        } catch (err) {
            setError(t("auth.login.shopifyAuthFailed"));
            track("login_failed", { method: "shopify", shop, error: err?.message || "Unknown" });
        } finally {
            setIsShopifyLoading(false);
        }
    }, [searchParams, shopifyAuth, setToken, handleGetUser, t]);

    React.useEffect(() => {
        if (user && user.id) {
            setIsTokenLoading(false);
            track("login_already_authenticated", {
                userId: user.id,
                userRoles: user.roles,
                isClient: user.roles.includes("client"),
                tutorialCompleted: user.tutorials?.welcome?.completed,
            });
            const defaultPath = user.roles.includes("client")
                ? "/client/workflows"
                : user.interfaceStyle === "expert"
                    ? "/workflows"
                    : "/chat";
            const redirectPath = from || defaultPath;
            track("login_redirect", {
                destination: redirectPath,
                userType: user.roles.includes("client")
                    ? "client"
                    : user.tutorials?.welcome?.completed
                        ? "returning_user"
                        : "new_user",
                source: "already_authenticated",
                hadStoredPath: !!from,
            });
            navigate(redirectPath);
        }
    }, [user, navigate, from]);

    React.useEffect(() => {
        if (!token || didTokenRequest.current) return;
        didTokenRequest.current = true;

        const flight = consumeOAuthFlight();
        if (!flight) {
            track("login_token_rejected_no_flight", {
                method: "oauth_callback",
                reason: "missing_or_expired_in_flight_marker",
            });
            setError(t("auth.loginV3.tokenRejectedNoFlight"));
            return;
        }

        setIsTokenLoading(true);
        setLastLoginMethod(flight.method);
        track("login_token_received", {
            method: "oauth_callback",
            initiatedAs: flight.method,
            hasToken: true,
        });
        setToken(token);
        setTimeout(() => {
            handleGetUser().catch((err) => {
                setError(err?.message || t("auth.login.authenticationFailed"));
                setIsTokenLoading(false);
            });
        }, 300);
    }, [token, setToken, handleGetUser, t]);

    React.useEffect(() => {
        const run = async () => {
            if (!magicToken || didMagicLinkRequest.current) return;
            didMagicLinkRequest.current = true;
            setIsMagicLinkLoading(true);
            track("login_attempt_started", { method: "magic_link" });
            try {
                const response = await authenticateWithMagicLink({ token: magicToken });
                if (response?.accessToken) {
                    setToken(response.accessToken);
                    setLastLoginMethod("email");
                    track("login_success", { method: "magic_link" });
                    await handleGetUser();
                } else {
                    const msg = response?.error || t("auth.login.magicLinkAuthFailed");
                    setError(msg);
                    track("login_failed", { method: "magic_link", error: msg });
                }
            } catch (err) {
                const msg = err?.message || t("auth.login.magicLinkAuthFailed");
                setError(msg);
                track("login_failed", { method: "magic_link", error: msg });
            } finally {
                setIsMagicLinkLoading(false);
            }
        };
        run();
    }, [magicToken, authenticateWithMagicLink, setToken, handleGetUser]);

    React.useEffect(() => {
        if (didShopifyAuthRequest.current) return;
        const hmac = searchParams.get("hmac");
        const host = searchParams.get("host");
        const shop = searchParams.get("shop");
        const timestamp = searchParams.get("timestamp");
        if (hmac && host && shop && timestamp) {
            didShopifyAuthRequest.current = true;
            handleShopifyAuth();
        }
    }, [searchParams, handleShopifyAuth]);

    const hasShopifyParams =
        searchParams.get("hmac") &&
        searchParams.get("host") &&
        searchParams.get("shop") &&
        searchParams.get("timestamp");

    if (isSSOLoading)
        return <LoginV3Loading title={t("auth.login.ssoConnecting")} subtitle={t("auth.login.ssoRedirecting")} />;
    if (isShopifyLoading)
        return <LoginV3Loading title={t("auth.login.shopifyAuthenticating")} subtitle={t("auth.login.shopifyVerifying")} />;
    if (isMagicLinkLoading)
        return <LoginV3Loading title={t("auth.login.loggingIn")} subtitle={t("auth.login.magicLinkAuthenticating")} />;
    if (
        isTokenLoading ||
        (token && isLoadingUser && !user) ||
        (isLoadingUser && !token && !user && !magicToken && !hasShopifyParams)
    )
        return <LoginV3Loading title={t("auth.login.loggingIn")} subtitle={t("auth.login.sessionAuthenticating")} />;

    const trustChips = [
        { icon: Lock, label: t("auth.loginV3.trust.sso") },
        { icon: ShieldCheck, label: t("auth.loginV3.trust.soc2") },
        { icon: FileText, label: t("auth.loginV3.trust.auditLogs") },
        { icon: Globe, label: t("auth.loginV3.trust.gdpr") },
    ];

    return (
        <div className="relative min-h-screen bg-white text-[#171a1f] font-sans antialiased overflow-x-hidden">
            <Helmet>
                <title>{`${t("auth.login.title")} - AgentUI`}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,200;12..96,300;12..96,400;12..96,500;12..96,600&display=swap"
                    rel="stylesheet"
                />
                <style>{`
                    .font-display { font-family: 'Bricolage Grotesque', -apple-system, BlinkMacSystemFont, system-ui, sans-serif; }
                    @keyframes cardIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
                    @media (prefers-reduced-motion: reduce) { .animate-\\[cardIn_420ms_cubic-bezier\\(0\\.22\\,1\\,0\\.36\\,1\\)_both\\] { animation: none !important; } }
                `}</style>
            </Helmet>

            <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
                <div
                    className="absolute -top-32 -right-44 w-[720px] h-[720px] rounded-full opacity-70 blur-[80px]"
                    style={{ background: "radial-gradient(circle, #d9c8ee 0%, rgba(217,200,238,0) 70%)" }}
                />
                <div
                    className="absolute -bottom-52 -right-20 w-[580px] h-[580px] rounded-full opacity-55 blur-[80px]"
                    style={{ background: "radial-gradient(circle, #f3d6e4 0%, rgba(243,214,228,0) 70%)" }}
                />
            </div>

            <div className="relative z-10 grid grid-rows-[auto_1fr_auto] min-h-screen px-5 pb-7 pt-5 md:px-8 md:pb-8 md:pt-7">
                <header className="w-full max-w-[1180px] mx-auto">
                    <nav
                        aria-label="Primary"
                        className="flex items-center justify-between gap-4 rounded-full bg-white border border-[#f1eff5] shadow-[0_1px_2px_rgb(23_26_31/0.04),0_6px_16px_-6px_rgb(23_26_31/0.08)] py-2.5 pl-5 pr-3 md:py-3 md:pl-6 md:pr-3.5"
                    >
                        <a href="/" aria-label="AgentUI home" className="inline-flex items-center text-[#171a1f]">
                            <Logo className="h-7 w-auto" />
                        </a>
                        <div className="flex items-center gap-2">
                            <a
                                href="https://help.agentui.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hidden sm:inline-flex rounded-full px-3.5 py-2 text-sm text-[#6b7280] hover:text-[#171a1f] hover:bg-[#fafafa] transition-colors"
                            >
                                {t("auth.loginV3.nav.needHelp")}
                            </a>
                            <a
                                href="https://agentui.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#e9e7ee] py-2 pl-3.5 pr-4 text-sm font-medium text-[#171a1f] hover:bg-[#fafafa] hover:border-[#d8d4e0] transition-colors"
                            >
                                {t("auth.loginV3.nav.backToSite")}
                                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.6} />
                            </a>
                        </div>
                    </nav>
                </header>

                <main className="flex items-center justify-center py-14 md:py-22">
                    <div className="w-full max-w-[480px] flex flex-col items-stretch">
                        <span className="self-center inline-flex items-center gap-2 rounded-full border border-[#e8def4] bg-[#f3eff9] py-1.5 pl-3 pr-3.5 text-[13px] text-[#7c6bb0] mb-7">
                            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.8} />
                            {t("auth.loginV3.welcomeBack")}
                        </span>
                        <h1 className="font-display text-center text-[clamp(40px,6vw,60px)] leading-[1.04] tracking-[-0.025em] font-light text-[#171a1f] mb-3.5 text-balance">
                            {t("auth.loginV3.titleStart")}
                            <span className="block font-light text-[#a594c8]">
                                {t("auth.loginV3.titleAccent")}
                            </span>
                        </h1>
                        <p className="text-center text-[15.5px] leading-[1.55] text-[#6b7280] max-w-[36ch] mx-auto mb-9">
                            {t("auth.loginV3.lede")}
                        </p>

                        <LoginV3Card
                            isTokenStep={isTokenStep}
                            email={email}
                            setEmail={setEmail}
                            error={error}
                            successMessage={successMessage}
                            onEmailSubmit={handleEmailSubmit}
                            onTokenSubmit={handleTokenSubmit}
                            onGoogle={handleGoogleSignIn}
                            onMicrosoft={handleMicrosoftSignIn}
                            onSso={handleSSOLogin}
                            onResend={handleResendCode}
                            onBack={() => {
                                setIsTokenStep(false);
                                setSuccessMessage(null);
                                setResendCountdown(0);
                                setResendAttempts(0);
                                sessionStorage.removeItem("login_otp_state");
                            }}
                            isGoogleLoading={isGoogleLoading}
                            isMicrosoftLoading={isMicrosoftLoading}
                            isSSOLoading={isSSOLoading}
                            isLoginLoading={isLoginLoading}
                            requestCodeIsLoading={requestCodeIsLoading}
                            isLoadingUser={isLoadingUser}
                            isResending={isResending}
                            ssoAvailable={ssoAvailable}
                            resendCountdown={resendCountdown}
                            loginError={loginError}
                            requestCodeError={requestCodeError}
                            lastUsedMethod={lastUsedMethod}
                        />

                        <p className="mt-5 text-center text-[13.5px] text-[#6b7280]">
                            {t("auth.loginV3.notMemberYet")}{" "}
                            <InternalLink
                                to="/signup"
                                className="font-medium text-[#171a1f] border-b-[1.5px] border-[#a594c8] pb-px hover:border-[#7c6bb0] transition-colors"
                            >
                                {t("auth.loginV3.createAccount")}
                            </InternalLink>
                        </p>

                        <div className="flex flex-wrap justify-center gap-2 mt-7" role="list" aria-label="Security and compliance">
                            {trustChips.map(({ icon: Icon, label }) => (
                                <span
                                    key={label}
                                    role="listitem"
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#f1eff5] shadow-[0_1px_2px_rgb(23_26_31/0.03)] py-1.5 pl-3 pr-3.5 text-[12.5px] text-[#6b7280]"
                                >
                                    <Icon className="w-3 h-3 text-[#7c6bb0] flex-shrink-0" strokeWidth={1.8} />
                                    {label}
                                </span>
                            ))}
                        </div>

                        <AuthLanguagePicker />

                        <AuthFaq />
                    </div>
                </main>

                <footer className="max-w-[760px] w-full mx-auto py-4 px-2 text-xs text-[#9ca3af] text-center">
                    <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2">
                        <a href="https://agentui.ai/en/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-[#6b7280] transition-colors">
                            {t("auth.loginV3.footer.privacy")}
                        </a>
                        <span className="opacity-50">·</span>
                        <a href="https://agentui.ai/en/terms" target="_blank" rel="noopener noreferrer" className="hover:text-[#6b7280] transition-colors">
                            {t("auth.loginV3.footer.terms")}
                        </a>
                        <span className="opacity-50">·</span>
                        <a href="https://app.agentui.ai/status" target="_blank" rel="noopener noreferrer" className="hover:text-[#6b7280] transition-colors">
                            {t("auth.loginV3.footer.status")}
                        </a>
                    </div>
                    <p className="mt-3 max-w-[640px] mx-auto leading-5 text-[#9ca3af]">
                        {t("auth.loginV3.footer.aiServices")}
                    </p>
                    <p className="mt-2">
                        {t("auth.loginV3.footer.copyright", { year: new Date().getFullYear() })}
                    </p>
                </footer>
            </div>
        </div>
    );
}

export default LoginV3;
